import { Fragment, useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import {
  beginTwoFactorSetup,
  changeOwnPassword,
  disableTwoFactor,
  verifyTwoFactorCode,
} from "../api/authApi";
import PasswordInput from "../components/PasswordInput";
import TwoFactorQrCode from "../components/TwoFactorQrCode";
import SixDigitCodeInput from "../components/SixDigitCodeInput";
import DeleteButton from "../components/DeleteButton";
import EditButton from "../components/EditButton";
import UserRoleChip from "../components/UserRoleChip";
import {
  hasPermission,
  PERMISSION_LABELS,
  USER_ROLES,
} from "../auth/permissions";
import { getRoles } from "../api/rolesApi";
import {
  getSessionSettings,
  invalidateNonRootSessions,
  invalidateUserSessions,
  updateSessionSettings,
} from "../api/sessionManagementApi";
import {
  createUser,
  deleteUser,
  getUsers,
  updateUser,
  updateUserStatus,
  setPasswordChangeRequired,
} from "../api/usersApi";
const formatDateTime = (value) => {
  const date = new Date(value);
  const pad = (part) => String(part).padStart(2, "0");

  return `${pad(date.getDate())}.${pad(
    date.getMonth() + 1,
  )}.${date.getFullYear()} ${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}:${pad(date.getSeconds())}`;
};

const localDateValue = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (part) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}`;
};

const formatDateFilterValue = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  return `${day}.${month}.${year}`;
};

const parseDateFilterValue = (displayValue) => {
  const match = displayValue.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return "";
  const [, day, month, year] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (
    date.getFullYear() !== Number(year) ||
    date.getMonth() !== Number(month) - 1 ||
    date.getDate() !== Number(day)
  )
    return "";
  return `${year}-${month}-${day}`;
};

const PERMISSION_GROUPS = [
  {
    label: "Starters",
    permissions: [
      "STARTERS_READ",
      "STARTERS_CREATE",
      "STARTERS_UPDATE",
      "STARTERS_DELETE",
      "STARTERS_VISIBILITY_MANAGE",
    ],
  },
  {
    label: "Armatures",
    permissions: [
      "ARMATURES_READ",
      "ARMATURES_CREATE",
      "ARMATURES_UPDATE",
      "ARMATURES_DELETE",
      "ARMATURES_VISIBILITY_MANAGE",
    ],
  },
  {
    label: "Field Cases",
    permissions: [
      "FIELD_CASES_READ",
      "FIELD_CASES_CREATE",
      "FIELD_CASES_UPDATE",
      "FIELD_CASES_DELETE",
      "FIELD_CASES_VISIBILITY_MANAGE",
    ],
  },
  {
    label: "Accounts",
    permissions: [
      "USERS_READ",
      "USERS_CREATE",
      "USERS_UPDATE",
      "USERS_MANAGE_ROLES",
      "USERS_MANAGE_ADMINS",
      "ACCOUNTS_MANAGE_STATUS",
    ],
  },
  {
    label: "Security",
    permissions: ["USERS_ENFORCE_2FA", "SESSIONS_MANAGE"],
  },
  {
    label: "Documentation",
    permissions: ["API_DOCS_READ"],
  },
];

const isCurrentUser = (account, currentUser) => {
  const matchesId =
    account.userId &&
    currentUser?.userId &&
    account.userId === currentUser.userId;
  const accountEmail = account.email?.toLowerCase();
  const currentEmail = currentUser?.email?.toLowerCase();
  return Boolean(
    matchesId ||
      (accountEmail && currentEmail && accountEmail === currentEmail),
  );
};

const AccountPage = () => {
  const { user, updateCurrentUser } = useAuth();
  const canReadUsers = hasPermission(user, "USERS_READ");
  const canCreateUsers = hasPermission(user, "USERS_CREATE");
  const canUpdateUsers = hasPermission(user, "USERS_UPDATE");
  const canAssignRoles = hasPermission(user, "USERS_MANAGE_ROLES");
  const canManageAdmins = hasPermission(user, "USERS_MANAGE_ADMINS");
  const canManageStatus = hasPermission(user, "ACCOUNTS_MANAGE_STATUS");
  const canManageSessions = hasPermission(user, "SESSIONS_MANAGE");
  const [users, setUsers] = useState([]);
  const [accountFilters, setAccountFilters] = useState({
    email: "",
    roles: [],
    statuses: ["Active", "Inactive"],
    createdFrom: "",
    modifiedFrom: "",
  });
  const [accountSort, setAccountSort] = useState({
    key: null,
    direction: null,
  });
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [createUserError, setCreateUserError] = useState("");
  const [createPasswordError, setCreatePasswordError] = useState("");
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    role: "EDITOR",
  });
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ email: "", password: "" });
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);
  const [editUserError, setEditUserError] = useState("");
  const [editPasswordError, setEditPasswordError] = useState("");
  const [statusError, setStatusError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [userPendingDelete, setUserPendingDelete] = useState(null);
  const [updatingStatusUserId, setUpdatingStatusUserId] = useState(null);
  const [roles, setRoles] = useState([]);
  const [permissionSearch, setPermissionSearch] = useState("");
  const [roleError, setRoleError] = useState("");
  const [selectedUserRole, setSelectedUserRole] = useState("EDITOR");
  const [isInvalidatingSessions, setIsInvalidatingSessions] = useState(false);
  const [sessionMessage, setSessionMessage] = useState("");
  const [sessionError, setSessionError] = useState("");
  const [confirmSessionInvalidation, setConfirmSessionInvalidation] =
    useState(false);
  const [userPendingSessionInvalidation, setUserPendingSessionInvalidation] =
    useState(null);
  const [invalidatingUserId, setInvalidatingUserId] = useState(null);
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState("");
  const [sessionTimeoutRemainderSeconds, setSessionTimeoutRemainderSeconds] =
    useState("");
  const [isLoadingSessionSettings, setIsLoadingSessionSettings] =
    useState(false);
  const [isSavingSessionSettings, setIsSavingSessionSettings] = useState(false);
  const [sessionSettingsMessage, setSessionSettingsMessage] = useState("");
  const [sessionSettingsError, setSessionSettingsError] = useState("");
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmation: "",
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordRequirementError, setPasswordRequirementError] = useState("");
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isTwoFactorDialogOpen, setIsTwoFactorDialogOpen] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(
    Boolean(user.twoFactorEnabled),
  );
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorRecoveryPhrase, setTwoFactorRecoveryPhrase] = useState("");
  const [twoFactorSetupStep, setTwoFactorSetupStep] = useState("idle");
  const [isStartingTwoFactorSetup, setIsStartingTwoFactorSetup] =
    useState(false);
  const [isEnablingTwoFactor, setIsEnablingTwoFactor] = useState(false);
  const [showDisableTwoFactor, setShowDisableTwoFactor] = useState(false);
  const [disableTwoFactorForm, setDisableTwoFactorForm] = useState({
    currentPassword: "",
    code: "",
  });
  const [isDisablingTwoFactor, setIsDisablingTwoFactor] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState("");
  const [twoFactorMessage, setTwoFactorMessage] = useState("");

  useEffect(() => {
    setTwoFactorEnabled(Boolean(user.twoFactorEnabled));
  }, [user.twoFactorEnabled]);

  const handleStartTwoFactorSetup = async () => {
    setTwoFactorError("");
    setTwoFactorMessage("");
    setIsStartingTwoFactorSetup(true);
    try {
      const setup = await beginTwoFactorSetup();
      setTwoFactorSetup(setup);
      setTwoFactorCode("");
      setTwoFactorSetupStep("verify");
    } catch (error) {
      setTwoFactorError(
        error?.detail ||
          "Unable to start authenticator setup. Please try again.",
      );
    } finally {
      setIsStartingTwoFactorSetup(false);
    }
  };

  const handleEnableTwoFactor = async (event) => {
    event.preventDefault();
    setTwoFactorError("");
    setTwoFactorMessage("");
    setIsEnablingTwoFactor(true);
    try {
      const result = await verifyTwoFactorCode(twoFactorCode, "enable");
      setTwoFactorEnabled(true);
      updateCurrentUser({ twoFactorEnabled: true });
      setTwoFactorRecoveryPhrase(result.recoveryPhrase);
      setTwoFactorSetupStep("recovery");
      setTwoFactorMessage("Two-factor authentication is enabled.");
    } catch (error) {
      setTwoFactorError(
        error?.detail ||
          "That code could not be verified. Check the code and try again.",
      );
    } finally {
      setIsEnablingTwoFactor(false);
    }
  };

  const handleDisableTwoFactor = async (event) => {
    event.preventDefault();
    setTwoFactorError("");
    setTwoFactorMessage("");
    setIsDisablingTwoFactor(true);
    try {
      await disableTwoFactor(
        disableTwoFactorForm.currentPassword,
        disableTwoFactorForm.code,
      );
      setTwoFactorEnabled(false);
      updateCurrentUser({ twoFactorEnabled: false });
      setDisableTwoFactorForm({ currentPassword: "", code: "" });
      setShowDisableTwoFactor(false);
      setTwoFactorRecoveryPhrase("");
      setTwoFactorMessage(
        "Two-factor authentication is disabled. Set it up again to create a new authenticator and recovery phrase.",
      );
    } catch (error) {
      setTwoFactorError(
        error?.detail ||
          "Unable to disable two-factor authentication. Check your password and code.",
      );
    } finally {
      setIsDisablingTwoFactor(false);
    }
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    setPasswordMessage("");
    setPasswordError("");
    setPasswordRequirementError("");
    if (passwordForm.newPassword.length < 12) {
      setPasswordRequirementError(
        "The new password must contain at least 12 characters.",
      );
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmation) {
      setPasswordError("The new passwords do not match.");
      return;
    }

    setIsChangingPassword(true);
    try {
      await changeOwnPassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
      );
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmation: "",
      });
      setPasswordMessage(
        "Your password has been changed. You can keep using this session; use your new password the next time you log in.",
      );
    } catch (error) {
      setPasswordError(
        error?.detail ||
          "Unable to change your password. Check your current password and try again.",
      );
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleInvalidateSessions = async () => {
    if (isInvalidatingSessions) return;
    setSessionMessage("");
    setSessionError("");
    setIsInvalidatingSessions(true);
    try {
      const result = await invalidateNonRootSessions();
      setSessionMessage(
        `Invalidated ${result.revokedSessionCount} non-root user session${
          result.revokedSessionCount === 1 ? "" : "s"
        }.`,
      );
      setConfirmSessionInvalidation(false);
    } catch (error) {
      setSessionError(error?.detail || "Unable to invalidate user sessions.");
    } finally {
      setIsInvalidatingSessions(false);
    }
  };

  const handleInvalidateUserSessions = async (account) => {
    if (invalidatingUserId) return;
    setSessionMessage("");
    setSessionError("");
    setInvalidatingUserId(account.userId);
    try {
      const result = await invalidateUserSessions(account.userId);
      setSessionMessage(
        `Invalidated ${result.revokedSessionCount} session${
          result.revokedSessionCount === 1 ? "" : "s"
        } for ${account.email}.`,
      );
      setUserPendingSessionInvalidation(null);
    } catch (error) {
      setSessionError(
        error?.detail || `Unable to invalidate sessions for ${account.email}.`,
      );
    } finally {
      setInvalidatingUserId(null);
    }
  };

  const closeCreateDialog = () => {
    if (isCreatingUser) return;
    setIsCreateDialogOpen(false);
    setCreateUserError("");
    setCreatePasswordError("");
    setNewUser({ email: "", password: "", role: "EDITOR" });
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setCreateUserError("");
    setCreatePasswordError("");
    if (newUser.password.length < 12) {
      setCreatePasswordError(
        "The temporary password must contain at least 12 characters.",
      );
      return;
    }
    setIsCreatingUser(true);

    try {
      await createUser(newUser);
      setIsCreateDialogOpen(false);
      setNewUser({ email: "", password: "", role: "EDITOR" });

      setIsLoadingUsers(true);
      try {
        setUsers(await getUsers());
        setUsersError("");
      } catch {
        setUsersError(
          "The account was created, but the account list could not be refreshed.",
        );
      } finally {
        setIsLoadingUsers(false);
      }
    } catch (error) {
      setCreateUserError(
        error?.detail ||
          "Unable to create the account. Please check the details and try again.",
      );
    } finally {
      setIsCreatingUser(false);
    }
  };

  const openEditDialog = (account) => {
    setEditingUser(account);
    setEditUserForm({
      email: account.email,
      password: "",
      mustChangePassword: Boolean(account.mustChangePassword),
    });
    setSelectedUserRole(account.role || "EDITOR");
    setEditUserError("");
    setEditPasswordError("");
  };

  const closeEditDialog = () => {
    if (isUpdatingUser) return;
    setEditingUser(null);
    setEditUserError("");
  };

  const handleUpdateUser = async (event) => {
    event.preventDefault();
    setEditUserError("");
    setEditPasswordError("");
    if (
      canUpdateUsers &&
      editUserForm.password &&
      editUserForm.password.length < 12
    ) {
      setEditPasswordError(
        "The new password must contain at least 12 characters.",
      );
      return;
    }
    setIsUpdatingUser(true);

    try {
      if (canUpdateUsers) {
        await updateUser(editingUser.userId, {
          email: editUserForm.email,
          password: editUserForm.password || null,
          ...(canAssignRoles ? { role: selectedUserRole } : {}),
        });
      } else if (canAssignRoles && selectedUserRole !== editingUser.role) {
        await updateUser(editingUser.userId, {
          email: editUserForm.email,
          role: selectedUserRole,
        });
      }
      if (
        canUpdateUsers &&
        editUserForm.mustChangePassword !==
          Boolean(editingUser.mustChangePassword)
      ) {
        await setPasswordChangeRequired(
          editingUser.userId,
          editUserForm.mustChangePassword,
        );
      }
      setEditingUser(null);
      setIsLoadingUsers(true);
      try {
        setUsers(await getUsers());
        setUsersError("");
      } catch {
        setUsersError(
          "The account was updated, but the account list could not be refreshed.",
        );
      } finally {
        setIsLoadingUsers(false);
      }
    } catch (error) {
      setEditUserError(
        error?.detail ||
          "Unable to update the account. Please check the details and try again.",
      );
    } finally {
      setIsUpdatingUser(false);
    }
  };

  const handleDeleteUser = async () => {
    const account = userPendingDelete;
    if (!account || deletingUserId) return;
    setDeleteError("");
    setDeletingUserId(account.userId);
    try {
      await deleteUser(account.userId);
      setUsers((current) =>
        current.filter((item) => item.userId !== account.userId),
      );
      setUserPendingDelete(null);
    } catch (error) {
      setDeleteError(error?.detail || "Unable to delete the account.");
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleToggleUserStatus = async (account) => {
    setStatusError("");
    setUpdatingStatusUserId(account.userId);

    try {
      const updatedAccount = await updateUserStatus(
        account.userId,
        !account.isActive,
      );
      setUsers((currentUsers) =>
        currentUsers.map((currentAccount) =>
          currentAccount.userId === updatedAccount.userId
            ? updatedAccount
            : currentAccount,
        ),
      );
    } catch (error) {
      setStatusError(
        error?.detail || "Unable to update account status. Please try again.",
      );
    } finally {
      setUpdatingStatusUserId(null);
    }
  };

  useEffect(() => {
    if (!canReadUsers) {
      return;
    }

    let isMounted = true;
    setIsLoadingUsers(true);

    getUsers()
      .then((result) => {
        if (isMounted) {
          setUsers(result);
          setUsersError("");
        }
      })
      .catch(() => {
        if (isMounted) {
          setUsersError("Unable to load accounts. Please try again later.");
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingUsers(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [canReadUsers]);

  useEffect(() => {
    if (!canAssignRoles) return;
    let isMounted = true;
    getRoles()
      .then((roleResults) => {
        if (!isMounted) return;
        setRoles(roleResults);
      })
      .catch((error) => {
        if (isMounted)
          setRoleError(error?.detail || "Unable to load role settings.");
      });
    return () => {
      isMounted = false;
    };
  }, [canAssignRoles]);

  useEffect(() => {
    if (!canManageSessions) return undefined;
    let isMounted = true;
    setIsLoadingSessionSettings(true);
    getSessionSettings()
      .then(({ timeoutSeconds }) => {
        if (isMounted) {
          setSessionTimeoutMinutes(String(Math.floor(timeoutSeconds / 60)));
          setSessionTimeoutRemainderSeconds(String(timeoutSeconds % 60));
        }
      })
      .catch((error) => {
        if (isMounted)
          setSessionSettingsError(
            error?.detail || "Unable to load session settings.",
          );
      })
      .finally(() => {
        if (isMounted) setIsLoadingSessionSettings(false);
      });
    return () => {
      isMounted = false;
    };
  }, [canManageSessions]);

  const handleSaveSessionSettings = async (event) => {
    event.preventDefault();
    const minutes = Number(sessionTimeoutMinutes || 0);
    const seconds = Number(sessionTimeoutRemainderSeconds || 0);
    const timeoutSeconds = minutes * 60 + seconds;
    if (
      (!sessionTimeoutMinutes && !sessionTimeoutRemainderSeconds) ||
      !Number.isInteger(minutes) ||
      !Number.isInteger(seconds) ||
      timeoutSeconds < 120 ||
      timeoutSeconds > 2_592_000
    ) {
      setSessionSettingsError("Enter a duration from 2 minutes to 30 days.");
      setSessionSettingsMessage("");
      return;
    }
    setIsSavingSessionSettings(true);
    setSessionSettingsError("");
    setSessionSettingsMessage("");
    try {
      const result = await updateSessionSettings(timeoutSeconds);
      setSessionTimeoutMinutes(String(Math.floor(result.timeoutSeconds / 60)));
      setSessionTimeoutRemainderSeconds(String(result.timeoutSeconds % 60));
      setSessionSettingsMessage("Session timeout updated.");
    } catch (error) {
      setSessionSettingsError(
        error?.detail || "Unable to update the session timeout.",
      );
    } finally {
      setIsSavingSessionSettings(false);
    }
  };

  const visibleUsers = users
    .filter((account) => {
      const status = account.isActive ? "Active" : "Inactive";
      const createdDate = localDateValue(account.createdDateTime);
      const modifiedDate = localDateValue(account.lastModifiedDateTime);
      return (
        account.email
          .toLowerCase()
          .includes(accountFilters.email.trim().toLowerCase()) &&
        (accountFilters.roles.length === 0 ||
          accountFilters.roles.includes(account.role)) &&
        accountFilters.statuses.includes(status) &&
        (!accountFilters.createdFrom ||
          Boolean(createdDate && createdDate >= accountFilters.createdFrom)) &&
        (!accountFilters.modifiedFrom ||
          Boolean(modifiedDate && modifiedDate >= accountFilters.modifiedFrom))
      );
    })
    .sort((left, right) => {
      if (!accountSort.key) return 0;
      const valueFor = (account) => {
        if (accountSort.key === "role") return account.role || "";
        if (accountSort.key === "status")
          return account.isActive ? "Active" : "Inactive";
        if (
          accountSort.key === "createdDateTime" ||
          accountSort.key === "lastModifiedDateTime"
        ) {
          return Date.parse(account[accountSort.key]) || 0;
        }
        return account.email || "";
      };
      const first = valueFor(left);
      const second = valueFor(right);
      const comparison =
        typeof first === "number"
          ? first - second
          : first.localeCompare(second, undefined, { sensitivity: "base" });
      return accountSort.direction === "asc" ? comparison : -comparison;
    });

  const toggleAccountSort = (key) => {
    setAccountSort((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const resetAccountTable = () => {
    setAccountFilters({
      email: "",
      roles: [],
      statuses: ["Active", "Inactive"],
      createdFrom: "",
      modifiedFrom: "",
    });
    setAccountSort({ key: null, direction: null });
  };

  const permissionMatrix = [
    ...new Set(roles.flatMap(({ permissions }) => permissions)),
  ].sort((left, right) =>
    (PERMISSION_LABELS[left] || left).localeCompare(
      PERMISSION_LABELS[right] || right,
    ),
  );
  const permissionsByGroup = PERMISSION_GROUPS.map((group) => ({
    ...group,
    permissions: permissionMatrix.filter((permission) =>
      group.permissions.includes(permission),
    ),
  })).filter((group) => group.permissions.length > 0);
  const groupedPermissionKeys = new Set(
    PERMISSION_GROUPS.flatMap(({ permissions }) => permissions),
  );
  const ungroupedPermissions = permissionMatrix.filter(
    (permission) => !groupedPermissionKeys.has(permission),
  );
  if (ungroupedPermissions.length > 0) {
    permissionsByGroup.push({
      label: "Other",
      permissions: ungroupedPermissions,
    });
  }
  const normalizedPermissionSearch = permissionSearch
    .trim()
    .toLocaleLowerCase();
  const permissionMatchesSearch = (permission) =>
    !normalizedPermissionSearch ||
    `${PERMISSION_LABELS[permission] || permission} ${permission}`
      .toLocaleLowerCase()
      .includes(normalizedPermissionSearch);
  const matchingPermissionCount = permissionMatrix.filter(
    permissionMatchesSearch,
  ).length;

  return (
    <main className="mx-auto w-full max-w-screen-2xl px-4 py-7 sm:px-6">
      <div className="mb-7 border-b border-zinc-200 pb-5">
        <p className="mb-1 text-sm font-semibold uppercase tracking-[0.14em] text-blue-700">
          Workspace settings
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-zinc-950">
          Account
        </h1>
        <p className="mt-1 text-sm text-zinc-600">
          Your profile, security, and portal access.
        </p>
      </div>

      <section
        aria-label="Account overview"
        className="grid gap-4 lg:grid-cols-[1.1fr_1fr_1fr]"
      >
        <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-800">
            Signed in as
          </p>
          <p className="mt-2 break-all text-lg font-semibold text-zinc-950">
            {user.email}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-sm text-zinc-600">Your role</span>
            <UserRoleChip role={user.role} />
          </div>
        </div>

        <div className="flex flex-col justify-between rounded-xl border border-zinc-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center lg:flex-col lg:items-start">
          <div>
            <h2 className="font-semibold text-zinc-900">Password</h2>
            <p className="mt-1 text-sm text-zinc-600">
              Update the password you use to sign in.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setPasswordError("");
              setPasswordMessage("");
              setIsPasswordDialogOpen(true);
            }}
            className="mt-4 w-fit rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-800 hover:bg-zinc-50 sm:mt-0 lg:mt-4"
          >
            Change password
          </button>
        </div>

        <div className="flex flex-col justify-between rounded-xl border border-zinc-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center lg:flex-col lg:items-start">
          <div>
            <h2 className="font-semibold text-zinc-900">
              Two-factor authentication
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                  twoFactorEnabled
                    ? "bg-green-100 text-green-800"
                    : "bg-zinc-100 text-zinc-700"
                }`}
              >
                {twoFactorEnabled ? "Enabled" : "Not enabled"}
              </span>
              {user.twoFactorRequired && (
                <span className="text-xs text-amber-800">
                  Required by policy
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setTwoFactorError("");
              setTwoFactorMessage("");
              setIsTwoFactorDialogOpen(true);
              if (!twoFactorEnabled && twoFactorSetupStep === "idle")
                handleStartTwoFactorSetup();
            }}
            className="mt-4 w-fit rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-800 hover:bg-zinc-50 sm:mt-0 lg:mt-4"
          >
            {twoFactorEnabled ? "Manage 2FA" : "Set up authenticator"}
          </button>
        </div>
      </section>

      {isTwoFactorDialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget &&
              !isEnablingTwoFactor &&
              !isDisablingTwoFactor
            )
              setIsTwoFactorDialogOpen(false);
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="two-factor-heading"
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl sm:p-6"
          >
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h2
                  id="two-factor-heading"
                  className="text-xl font-semibold text-zinc-900"
                >
                  Two-factor authentication
                </h2>
                <p className="mt-1 text-sm text-zinc-600">
                  Use an authenticator code in addition to your password.
                </p>
              </div>
              <button
                type="button"
                aria-label="Close dialog"
                onClick={() => setIsTwoFactorDialogOpen(false)}
                disabled={isEnablingTwoFactor || isDisablingTwoFactor}
                className="rounded-md px-2 py-1 text-2xl leading-none text-zinc-500 hover:bg-zinc-100 disabled:opacity-50"
              >
                ×
              </button>
            </div>
            <div className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium text-zinc-800">Status:</span>
                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                    twoFactorEnabled
                      ? "bg-green-100 text-green-800"
                      : "bg-zinc-100 text-zinc-700"
                  }`}
                >
                  {twoFactorEnabled ? "Enabled" : "Not enabled"}
                </span>
                {user.twoFactorRequired && (
                  <span className="text-amber-800">
                    Required by your account policy
                  </span>
                )}
              </div>

              {twoFactorEnabled &&
                !user.twoFactorRequired &&
                !showDisableTwoFactor && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowDisableTwoFactor(true);
                      setTwoFactorError("");
                      setTwoFactorMessage("");
                    }}
                    className="rounded-md border border-red-300 px-4 py-2.5 text-sm font-medium text-red-800 hover:bg-red-50"
                  >
                    Disable 2FA
                  </button>
                )}

              {twoFactorEnabled && showDisableTwoFactor && (
                <form
                  onSubmit={handleDisableTwoFactor}
                  className="space-y-4 border-t border-zinc-200 pt-4"
                >
                  <p className="text-sm text-zinc-600">
                    Enter your password and current authenticator code to
                    disable 2FA. Your authenticator and recovery phrase will be
                    removed.
                  </p>
                  <div className="max-w-sm space-y-1">
                    <label
                      htmlFor="disable-two-factor-password"
                      className="block text-sm font-medium text-zinc-700"
                    >
                      Current password
                    </label>
                    <PasswordInput
                      id="disable-two-factor-password"
                      name="currentPassword"
                      autoComplete="current-password"
                      required
                      value={disableTwoFactorForm.currentPassword}
                      onChange={(event) =>
                        setDisableTwoFactorForm((current) => ({
                          ...current,
                          currentPassword: event.target.value,
                        }))
                      }
                      disabled={isDisablingTwoFactor}
                      className="w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm shadow-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 disabled:bg-zinc-100"
                    />
                  </div>
                  <div className="max-w-xs space-y-1">
                    <SixDigitCodeInput
                      idPrefix="disable-two-factor-code"
                      label="Current authenticator code"
                      value={disableTwoFactorForm.code}
                      onChange={(code) =>
                        setDisableTwoFactorForm((current) => ({
                          ...current,
                          code,
                        }))
                      }
                      disabled={isDisablingTwoFactor}
                    />
                    {process.env.REACT_APP_MOCK_API === "true" && (
                      <p className="text-sm text-amber-800">
                        Dev mock TOTP code: 123456
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="submit"
                      disabled={
                        isDisablingTwoFactor ||
                        disableTwoFactorForm.code.length !== 6 ||
                        !disableTwoFactorForm.currentPassword
                      }
                      className="rounded-md bg-red-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-50"
                    >
                      {isDisablingTwoFactor
                        ? "Disabling…"
                        : "Confirm disable 2FA"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowDisableTwoFactor(false);
                        setDisableTwoFactorForm({
                          currentPassword: "",
                          code: "",
                        });
                        setTwoFactorError("");
                      }}
                      disabled={isDisablingTwoFactor}
                      className="rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {twoFactorSetupStep === "idle" && !twoFactorEnabled && (
                <button
                  type="button"
                  onClick={handleStartTwoFactorSetup}
                  disabled={isStartingTwoFactorSetup}
                  className="rounded-md bg-blue-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-900 disabled:opacity-50"
                >
                  {isStartingTwoFactorSetup ? "Starting setup…" : "Retry setup"}
                </button>
              )}

              {twoFactorSetupStep === "verify" && twoFactorSetup && (
                <form
                  onSubmit={handleEnableTwoFactor}
                  autoComplete="off"
                  data-1p-ignore="true"
                  data-bwignore="true"
                  data-lpignore="true"
                  className="space-y-4 border-t border-zinc-200 pt-4"
                >
                  <div className="space-y-2 text-sm">
                    <h3 className="font-medium text-zinc-900">
                      Add the account to your authenticator app
                    </h3>
                    {process.env.REACT_APP_MOCK_API === "true" ? (
                      <p className="text-amber-800">
                        Dev mock uses the fixed code 123456. Its sample seed
                        does not generate real authenticator codes.
                      </p>
                    ) : (
                      <>
                        <p className="text-zinc-600">
                          Choose a time-based account with six digits and a
                          30-second interval, then scan this QR code:
                        </p>
                        <TwoFactorQrCode
                          provisioningUri={twoFactorSetup.provisioningUri}
                        />
                        <p className="font-medium text-zinc-900">
                          Or enter the key manually:
                        </p>
                        <code className="block break-all rounded-md bg-zinc-50 p-3 font-mono text-base font-semibold text-zinc-900">
                          {twoFactorSetup.secret}
                        </code>
                        <details>
                          <summary className="cursor-pointer text-blue-800">
                            Show provisioning link
                          </summary>
                          <code className="mt-2 block break-all rounded-md bg-zinc-50 p-3 text-xs text-zinc-700">
                            {twoFactorSetup.provisioningUri}
                          </code>
                        </details>
                      </>
                    )}
                  </div>
                  <div className="max-w-xs space-y-1">
                    <SixDigitCodeInput
                      idPrefix="account-two-factor-code"
                      label="Authenticator setup verification code"
                      value={twoFactorCode}
                      onChange={setTwoFactorCode}
                      disabled={isEnablingTwoFactor}
                    />
                    {process.env.REACT_APP_MOCK_API === "true" && (
                      <p className="text-sm text-amber-800">
                        Dev mock TOTP code: 123456
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="submit"
                      disabled={
                        isEnablingTwoFactor || twoFactorCode.length !== 6
                      }
                      className="rounded-md bg-blue-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-900 disabled:opacity-50"
                    >
                      {isEnablingTwoFactor ? "Verifying…" : "Verify and enable"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTwoFactorSetupStep("idle");
                        setTwoFactorSetup(null);
                        setTwoFactorCode("");
                        setTwoFactorError("");
                      }}
                      disabled={isEnablingTwoFactor}
                      className="rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                    >
                      Cancel setup
                    </button>
                  </div>
                </form>
              )}

              {twoFactorSetupStep === "recovery" && (
                <div className="space-y-3 border-t border-zinc-200 pt-4">
                  <h3 className="font-medium text-zinc-900">
                    Save your one-time recovery phrase
                  </h3>
                  <p className="text-sm text-zinc-600">
                    You can use this phrase once if you lose access to your
                    authenticator. It won’t be shown again.
                  </p>
                  <code className="block break-all rounded-md border border-amber-300 bg-amber-50 p-3 font-mono text-base font-semibold tracking-wide text-zinc-900">
                    {twoFactorRecoveryPhrase}
                  </code>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(
                            twoFactorRecoveryPhrase,
                          );
                          setTwoFactorMessage(
                            "Recovery phrase copied. Store it somewhere safe.",
                          );
                        } catch {
                          setTwoFactorError(
                            "Clipboard access is unavailable. Copy the phrase manually before continuing.",
                          );
                        }
                      }}
                      className="rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                    >
                      Copy recovery phrase
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTwoFactorSetupStep("idle");
                        setTwoFactorSetup(null);
                        setTwoFactorRecoveryPhrase("");
                        setTwoFactorCode("");
                        setIsTwoFactorDialogOpen(false);
                      }}
                      className="rounded-md bg-blue-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-900"
                    >
                      I saved it
                    </button>
                  </div>
                </div>
              )}

              {twoFactorMessage && (
                <p className="text-sm text-green-800" role="status">
                  {twoFactorMessage}
                </p>
              )}
              {twoFactorError && (
                <p className="text-sm text-red-800" role="alert">
                  {twoFactorError}
                </p>
              )}
            </div>
          </section>
        </div>
      )}

      {isPasswordDialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isChangingPassword)
              setIsPasswordDialogOpen(false);
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="change-password-heading"
            className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl sm:p-6"
          >
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h2
                  id="change-password-heading"
                  className="text-xl font-semibold text-zinc-900"
                >
                  Change password
                </h2>
                <p className="mt-1 text-sm text-zinc-600">
                  Choose a new password with at least 12 characters.
                </p>
              </div>
              <button
                type="button"
                aria-label="Close dialog"
                onClick={() => setIsPasswordDialogOpen(false)}
                disabled={isChangingPassword}
                className="rounded-md px-2 py-1 text-2xl leading-none text-zinc-500 hover:bg-zinc-100 disabled:opacity-50"
              >
                ×
              </button>
            </div>
            <form
              onSubmit={handleChangePassword}
              noValidate
              className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4"
            >
              <div>
                <label
                  htmlFor="current-password"
                  className="mb-1 block text-sm font-medium text-zinc-700"
                >
                  Current password
                </label>
                <PasswordInput
                  id="current-password"
                  name="currentPassword"
                  autoComplete="current-password"
                  required
                  value={passwordForm.currentPassword}
                  onChange={(event) =>
                    setPasswordForm((current) => ({
                      ...current,
                      currentPassword: event.target.value,
                    }))
                  }
                  disabled={isChangingPassword}
                  className="w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-100 disabled:bg-zinc-100"
                />
              </div>
              <div>
                <label
                  htmlFor="new-password"
                  className="mb-1 block text-sm font-medium text-zinc-700"
                >
                  New password
                </label>
                <PasswordInput
                  id="new-password"
                  name="newPassword"
                  autoComplete="new-password"
                  minLength={12}
                  required
                  value={passwordForm.newPassword}
                  onChange={(event) =>
                    setPasswordForm((current) => ({
                      ...current,
                      newPassword: event.target.value,
                    }))
                  }
                  disabled={isChangingPassword}
                  aria-invalid={Boolean(passwordRequirementError)}
                  aria-describedby={
                    passwordRequirementError ? "new-password-error" : undefined
                  }
                  className={`w-full rounded-md border px-3 py-2.5 text-sm shadow-sm outline-none transition focus:ring-2 disabled:bg-zinc-100 ${
                    passwordRequirementError
                      ? "border-red-600 focus:border-red-600 focus:ring-red-100"
                      : "border-zinc-300 focus:border-blue-600 focus:ring-blue-100"
                  }`}
                />
                {passwordRequirementError && (
                  <p
                    id="new-password-error"
                    className="mt-1 text-sm text-red-700"
                    role="alert"
                  >
                    {passwordRequirementError}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="confirm-new-password"
                  className="mb-1 block text-sm font-medium text-zinc-700"
                >
                  Confirm new password
                </label>
                <PasswordInput
                  id="confirm-new-password"
                  name="confirmNewPassword"
                  autoComplete="new-password"
                  minLength={12}
                  required
                  value={passwordForm.confirmation}
                  onChange={(event) =>
                    setPasswordForm((current) => ({
                      ...current,
                      confirmation: event.target.value,
                    }))
                  }
                  disabled={isChangingPassword}
                  className="w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-100 disabled:bg-zinc-100"
                />
              </div>
              {passwordMessage && (
                <p className="text-sm text-green-800" role="status">
                  {passwordMessage}
                </p>
              )}
              {passwordError && (
                <p className="text-sm text-red-800" role="alert">
                  {passwordError}
                </p>
              )}
              <button
                type="submit"
                disabled={isChangingPassword}
                className="rounded-md bg-blue-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-900 disabled:opacity-50"
              >
                {isChangingPassword ? "Changing password…" : "Change password"}
              </button>
            </form>
          </section>
        </div>
      )}

      {canManageSessions && (
        <section className="mt-8" aria-labelledby="session-management-heading">
          <h2
            id="session-management-heading"
            className="mb-3 text-xl font-semibold text-zinc-900"
          >
            Session management
          </h2>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(18rem,1fr)]">
            <form
              onSubmit={handleSaveSessionSettings}
              className="rounded-lg border border-zinc-200 bg-white p-4"
            >
              <h3 className="mb-1 text-sm font-medium text-zinc-900">
                Session inactivity timeout
              </h3>
              <p
                id="session-timeout-help"
                className="mb-3 text-sm text-zinc-600"
              >
                Users are signed out after this period of inactivity. Enter
                minutes, seconds, or both (2 minutes to 30 days).
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label
                    htmlFor="session-timeout-minutes"
                    className="mb-1 block text-xs font-medium text-zinc-600"
                  >
                    Minutes
                  </label>
                  <input
                    id="session-timeout-minutes"
                    type="text"
                    inputMode="numeric"
                    value={sessionTimeoutMinutes}
                    onChange={(event) =>
                      setSessionTimeoutMinutes(
                        event.target.value.replace(/\D/g, ""),
                      )
                    }
                    disabled={
                      isLoadingSessionSettings || isSavingSessionSettings
                    }
                    aria-describedby="session-timeout-help"
                    className="w-28 rounded-md border border-zinc-300 px-3 py-2.5 text-sm tabular-nums shadow-sm outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-100 disabled:bg-zinc-100"
                  />
                </div>
                <div>
                  <label
                    htmlFor="session-timeout-seconds"
                    className="mb-1 block text-xs font-medium text-zinc-600"
                  >
                    Seconds
                  </label>
                  <input
                    id="session-timeout-seconds"
                    type="text"
                    inputMode="numeric"
                    value={sessionTimeoutRemainderSeconds}
                    onChange={(event) =>
                      setSessionTimeoutRemainderSeconds(
                        event.target.value.replace(/\D/g, ""),
                      )
                    }
                    disabled={
                      isLoadingSessionSettings || isSavingSessionSettings
                    }
                    aria-describedby="session-timeout-help"
                    className="w-28 rounded-md border border-zinc-300 px-3 py-2.5 text-sm tabular-nums shadow-sm outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-100 disabled:bg-zinc-100"
                  />
                </div>
                <button
                  type="submit"
                  disabled={
                    isLoadingSessionSettings ||
                    isSavingSessionSettings ||
                    (!sessionTimeoutMinutes && !sessionTimeoutRemainderSeconds)
                  }
                  className="rounded-md bg-blue-800 px-4 py-2 text-sm font-medium text-white hover:bg-blue-900 disabled:opacity-50"
                >
                  {isSavingSessionSettings ? "Saving…" : "Save timeout"}
                </button>
                {isLoadingSessionSettings && (
                  <span className="text-sm text-zinc-600" role="status">
                    Loading setting…
                  </span>
                )}
              </div>
              {sessionSettingsMessage && (
                <p className="mt-3 text-sm text-green-800" role="status">
                  {sessionSettingsMessage}
                </p>
              )}
              {sessionSettingsError && (
                <p className="mt-3 text-sm text-red-800" role="alert">
                  {sessionSettingsError}
                </p>
              )}
            </form>
            <div className="rounded-lg border border-red-200 bg-white p-4">
              <h3 className="font-medium text-zinc-900">
                Sign out all other users
              </h3>
              <p className="mb-4 mt-1 text-sm text-zinc-600">
                Immediately end every active non-root session.
              </p>
              {sessionMessage && (
                <p
                  className="mb-4 rounded-md bg-green-100 p-3 text-sm text-green-800"
                  role="status"
                >
                  {sessionMessage}
                </p>
              )}
              {sessionError && (
                <p
                  className="mb-4 rounded-md bg-red-100 p-3 text-sm text-red-800"
                  role="alert"
                >
                  {sessionError}
                </p>
              )}
              {!confirmSessionInvalidation ? (
                <button
                  type="button"
                  onClick={() => {
                    setSessionError("");
                    setSessionMessage("");
                    setConfirmSessionInvalidation(true);
                  }}
                  className="rounded-md bg-red-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-800"
                >
                  Invalidate sessions
                </button>
              ) : (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="text-sm text-red-900">
                    This will immediately sign out every non-root user with an
                    active session. Continue?
                  </p>
                  <div className="mt-4 flex flex-wrap justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setConfirmSessionInvalidation(false)}
                      disabled={isInvalidatingSessions}
                      className="rounded-md border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleInvalidateSessions}
                      disabled={isInvalidatingSessions}
                      className="rounded-md bg-red-700 px-4 py-2 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-50"
                    >
                      {isInvalidatingSessions
                        ? "Invalidating…"
                        : "Confirm invalidation"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {(canReadUsers || canAssignRoles) && (
        <section className="mt-8" aria-labelledby="manage-accounts-heading">
          <h2
            id="manage-accounts-heading"
            className="mb-4 text-xl font-semibold text-zinc-900"
          >
            Manage accounts
          </h2>
          <p className="-mt-2 mb-4 text-sm text-zinc-600">
            A required password change takes effect at the user’s next login.
            Their current sessions stay active.
          </p>

          {canCreateUsers && (
            <button
              type="button"
              onClick={() => {
                setCreateUserError("");
                setIsCreateDialogOpen(true);
              }}
              className="mb-4 rounded-md bg-blue-800 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-900"
            >
              Add user
            </button>
          )}

          {isLoadingUsers && (
            <p className="text-sm text-zinc-600" role="status">
              Loading accounts…
            </p>
          )}
          {usersError && (
            <p
              className="rounded-md bg-red-100 p-3 text-sm text-red-800"
              role="alert"
            >
              {usersError}
            </p>
          )}
          {statusError && (
            <p
              className="mb-4 rounded-md bg-red-100 p-3 text-sm text-red-800"
              role="alert"
            >
              {statusError}
            </p>
          )}
          {deleteError && (
            <p
              className="mb-4 rounded-md bg-red-100 p-3 text-sm text-red-800"
              role="alert"
            >
              {deleteError}
            </p>
          )}
          {canReadUsers &&
            !isLoadingUsers &&
            !usersError &&
            users.length === 0 && (
              <p className="text-sm text-zinc-600">No accounts found.</p>
            )}
          {canReadUsers &&
            !isLoadingUsers &&
            !usersError &&
            users.length > 0 && (
              <div>
                <div className="mb-2 flex justify-end">
                  <button
                    type="button"
                    onClick={resetAccountTable}
                    className="rounded border border-zinc-300 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
                  >
                    Reset filters and sorting
                  </button>
                </div>
                <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
                  <table className="w-max min-w-full table-auto divide-y divide-zinc-200 text-left text-sm">
                    <thead className="bg-zinc-50 text-xs font-medium uppercase tracking-wide text-zinc-600">
                      <tr>
                        {[
                          ["email", "Email address"],
                          ["role", "Role"],
                          ["status", "Status"],
                          ["createdDateTime", "Created"],
                          ["lastModifiedDateTime", "Last modified"],
                        ].map(([key, label]) => (
                          <th
                            key={key}
                            scope="col"
                            aria-sort={
                              accountSort.key === key
                                ? accountSort.direction === "asc"
                                  ? "ascending"
                                  : "descending"
                                : "none"
                            }
                            className="px-4 py-3 align-top"
                          >
                            <button
                              type="button"
                              aria-label={`Sort by ${label}`}
                              onClick={() => toggleAccountSort(key)}
                              className="mb-2 inline-flex items-center gap-1 text-left hover:text-zinc-900"
                            >
                              {label}
                              <span aria-hidden="true">
                                {accountSort.key === key
                                  ? accountSort.direction === "asc"
                                    ? "▲"
                                    : "▼"
                                  : "↕"}
                              </span>
                            </button>
                            {key === "email" && (
                              <input
                                type="search"
                                aria-label="Search Email address"
                                placeholder="Search"
                                value={accountFilters.email}
                                onChange={(event) =>
                                  setAccountFilters((current) => ({
                                    ...current,
                                    email: event.target.value,
                                  }))
                                }
                                className="mt-2 block w-full min-w-24 rounded border border-zinc-300 bg-white px-2 py-1 text-xs font-normal normal-case tracking-normal text-zinc-900 placeholder:text-zinc-400"
                              />
                            )}
                            {key === "role" && (
                              <details className="mt-2 text-xs font-normal normal-case tracking-normal">
                                <summary className="cursor-pointer rounded border border-zinc-300 bg-white px-2 py-1 text-zinc-700">
                                  {accountFilters.roles.length
                                    ? `${accountFilters.roles.length} selected`
                                    : "All roles"}
                                </summary>
                                <fieldset className="mt-1 space-y-1 rounded border border-zinc-200 bg-white p-2">
                                  <legend className="sr-only">
                                    Filter by roles
                                  </legend>
                                  {USER_ROLES.map((role) => (
                                    <label
                                      key={role}
                                      className="flex items-center gap-2 whitespace-nowrap text-zinc-700"
                                    >
                                      <input
                                        type="checkbox"
                                        aria-label={`Role filter: ${role}`}
                                        checked={accountFilters.roles.includes(
                                          role,
                                        )}
                                        onChange={(event) =>
                                          setAccountFilters((current) => ({
                                            ...current,
                                            roles: event.target.checked
                                              ? [...current.roles, role]
                                              : current.roles.filter(
                                                  (selectedRole) =>
                                                    selectedRole !== role,
                                                ),
                                          }))
                                        }
                                      />
                                      {role}
                                    </label>
                                  ))}
                                </fieldset>
                              </details>
                            )}
                            {key === "status" && (
                              <details className="mt-2 text-xs font-normal normal-case tracking-normal">
                                <summary className="cursor-pointer rounded border border-zinc-300 bg-white px-2 py-1 text-zinc-700">
                                  {accountFilters.statuses.length === 2
                                    ? "All statuses"
                                    : accountFilters.statuses.join(", ") ||
                                      "No statuses"}
                                </summary>
                                <fieldset className="mt-1 space-y-1 rounded border border-zinc-200 bg-white p-2">
                                  <legend className="sr-only">
                                    Filter by status
                                  </legend>
                                  {["Active", "Inactive"].map((status) => (
                                    <label
                                      key={status}
                                      className="flex items-center gap-2 whitespace-nowrap text-zinc-700"
                                    >
                                      <input
                                        type="checkbox"
                                        aria-label={`Status filter: ${status}`}
                                        checked={accountFilters.statuses.includes(
                                          status,
                                        )}
                                        onChange={(event) =>
                                          setAccountFilters((current) => ({
                                            ...current,
                                            statuses: event.target.checked
                                              ? [...current.statuses, status]
                                              : current.statuses.filter(
                                                  (value) => value !== status,
                                                ),
                                          }))
                                        }
                                      />
                                      {status}
                                    </label>
                                  ))}
                                </fieldset>
                              </details>
                            )}
                            {key === "createdDateTime" && (
                              <DateFilter
                                label="Created on or after"
                                value={accountFilters.createdFrom}
                                onChange={(value) =>
                                  setAccountFilters((current) => ({
                                    ...current,
                                    createdFrom: value,
                                  }))
                                }
                              />
                            )}
                            {key === "lastModifiedDateTime" && (
                              <DateFilter
                                label="Last modified on or after"
                                value={accountFilters.modifiedFrom}
                                onChange={(value) =>
                                  setAccountFilters((current) => ({
                                    ...current,
                                    modifiedFrom: value,
                                  }))
                                }
                              />
                            )}
                          </th>
                        ))}
                        <th scope="col" className="px-4 py-3">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-200 text-zinc-900">
                      {visibleUsers.length === 0 && (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-4 py-6 text-center text-sm text-zinc-500"
                          >
                            No accounts match these filters.
                          </td>
                        </tr>
                      )}
                      {visibleUsers.map((account) => (
                        <tr
                          key={account.userId}
                          className={
                            isCurrentUser(account, user)
                              ? "bg-indigo-50/70"
                              : "hover:bg-zinc-50"
                          }
                        >
                          <td
                            className={`border-l-4 px-4 py-3 ${
                              isCurrentUser(account, user)
                                ? "border-indigo-600 font-semibold text-indigo-950"
                                : "border-transparent"
                            }`}
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="break-all">{account.email}</span>
                              {isCurrentUser(account, user) && (
                                <span className="rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-xs font-semibold text-indigo-800">
                                  You · signed in
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <UserRoleChip role={account.role} />
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              role="switch"
                              aria-checked={account.isActive}
                              aria-label={`${
                                account.isActive ? "Deactivate" : "Activate"
                              } ${account.email}`}
                              disabled={
                                !canManageStatus ||
                                account.role === "ROOT" ||
                                (account.role === "ADMIN" &&
                                  !canManageAdmins) ||
                                updatingStatusUserId === account.userId
                              }
                              onClick={() => handleToggleUserStatus(account)}
                              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-zinc-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                                account.isActive
                                  ? "bg-green-600"
                                  : "bg-zinc-300"
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                  account.isActive
                                    ? "translate-x-6"
                                    : "translate-x-1"
                                }`}
                              />
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            {formatDateTime(account.createdDateTime)}
                          </td>
                          <td className="px-4 py-3">
                            {formatDateTime(account.lastModifiedDateTime)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <div className="flex gap-2">
                              {(canUpdateUsers || canAssignRoles) &&
                                account.role !== "ROOT" &&
                                (canManageAdmins ||
                                  account.role !== "ADMIN") && (
                                  <EditButton
                                    onClick={() => openEditDialog(account)}
                                    label={`Edit ${account.email}`}
                                    title="Edit user"
                                  />
                                )}
                              {canManageAdmins && account.role !== "ROOT" && (
                                <DeleteButton
                                  onClick={() => setUserPendingDelete(account)}
                                  label={`Delete ${account.email}`}
                                  title="Delete user"
                                  disabled={deletingUserId === account.userId}
                                />
                              )}
                              {canManageSessions && account.role !== "ROOT" && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSessionError("");
                                    setUserPendingSessionInvalidation(account);
                                  }}
                                  disabled={Boolean(invalidatingUserId)}
                                  className="rounded-md border border-red-300 px-2 py-1 text-xs font-medium text-red-800 hover:bg-red-50 disabled:opacity-50"
                                  aria-label={`Invalidate sessions for ${account.email}`}
                                  title="Invalidate sessions"
                                >
                                  Sign out
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
        </section>
      )}

      {canAssignRoles && (
        <section className="mt-8" aria-labelledby="role-permissions-heading">
          <h2
            id="role-permissions-heading"
            className="mb-2 text-xl font-semibold text-zinc-900"
          >
            Role permissions
          </h2>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-zinc-600">
              Each account has one role. Role permissions are fixed by the
              portal.
            </p>
            <p
              className="flex items-center gap-4 text-xs font-medium text-zinc-600"
              aria-label="Permission legend"
            >
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="inline-flex h-5 w-5 items-center justify-center rounded bg-emerald-100 font-bold text-emerald-800"
                >
                  ✓
                </span>{" "}
                Granted
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="inline-flex h-5 w-5 items-center justify-center rounded bg-zinc-100 text-zinc-500"
                >
                  ×
                </span>{" "}
                Not granted
              </span>
            </p>
          </div>
          <div className="mb-3 flex flex-wrap items-end gap-3">
            <label className="block w-full max-w-md">
              <span className="mb-1 block text-sm font-medium text-zinc-700">
                Find a permission
              </span>
              <input
                type="search"
                aria-label="Search permissions"
                placeholder="Search by name or permission code"
                value={permissionSearch}
                onChange={(event) => setPermissionSearch(event.target.value)}
                className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 shadow-sm outline-none placeholder:text-zinc-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
              />
            </label>
            <p className="pb-2 text-xs text-zinc-600" aria-live="polite">
              {permissionSearch.trim()
                ? `${matchingPermissionCount} matching ${
                    matchingPermissionCount === 1 ? "permission" : "permissions"
                  } highlighted; all rows remain visible.`
                : `${permissionMatrix.length} permissions`}
            </p>
          </div>
          {roleError && (
            <p
              className="mb-4 rounded-md bg-red-100 p-3 text-sm text-red-800"
              role="alert"
            >
              {roleError}
            </p>
          )}
          <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white">
            <table className="min-w-full divide-y divide-zinc-200 text-left text-sm">
              <thead className="bg-zinc-100">
                <tr>
                  <th
                    scope="col"
                    className="sticky left-0 z-10 min-w-56 bg-zinc-100 px-4 py-3 font-semibold text-zinc-800"
                  >
                    Permission
                  </th>
                  {roles.map(({ role }) => (
                    <th
                      key={role}
                      scope="col"
                      className="whitespace-nowrap px-4 py-3 text-center"
                    >
                      <UserRoleChip role={role} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {permissionsByGroup.map((group) => (
                  <Fragment key={group.label}>
                    <tr className="border-y border-zinc-200 bg-zinc-100">
                      <th
                        scope="rowgroup"
                        colSpan={roles.length + 1}
                        className="px-4 py-2 text-left text-xs font-bold uppercase tracking-wide text-zinc-700"
                      >
                        {group.label}
                      </th>
                    </tr>
                    {group.permissions.map((permission, rowIndex) => {
                      const isMatch =
                        Boolean(normalizedPermissionSearch) &&
                        permissionMatchesSearch(permission);
                      const isSearchActive = Boolean(
                        normalizedPermissionSearch,
                      );
                      return (
                        <tr
                          key={permission}
                          className={`${
                            isMatch
                              ? "bg-sky-50 ring-1 ring-inset ring-sky-200"
                              : rowIndex % 2 === 0
                              ? "bg-white"
                              : "bg-zinc-50/70"
                          } ${isSearchActive && !isMatch ? "opacity-45" : ""}`}
                        >
                          <th
                            scope="row"
                            className={`sticky left-0 px-4 py-2.5 text-left font-medium text-zinc-800 ${
                              isMatch
                                ? "bg-sky-50"
                                : rowIndex % 2 === 0
                                ? "bg-white"
                                : "bg-zinc-50"
                            }`}
                            title={permission}
                          >
                            {PERMISSION_LABELS[permission] || permission}
                          </th>
                          {roles.map(({ role, permissions }) => (
                            <td
                              key={role}
                              className="px-3 py-2 text-center"
                              aria-label={`${role}: ${
                                permissions.includes(permission)
                                  ? "granted"
                                  : "not granted"
                              }`}
                            >
                              <span
                                aria-hidden="true"
                                className={`mx-auto inline-flex h-6 w-7 items-center justify-center rounded font-bold ${
                                  permissions.includes(permission)
                                    ? isMatch
                                      ? "bg-emerald-200 text-emerald-950"
                                      : "bg-emerald-100 text-emerald-800"
                                    : "bg-zinc-100 text-zinc-400"
                                }`}
                              >
                                {permissions.includes(permission) ? "✓" : "×"}
                              </span>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {isCreateDialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeCreateDialog();
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-user-title"
            className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
          >
            <div className="mb-5 flex items-center justify-between gap-4">
              <h2
                id="create-user-title"
                className="text-xl font-semibold text-zinc-900"
              >
                Add user
              </h2>
              <button
                type="button"
                aria-label="Close dialog"
                onClick={closeCreateDialog}
                disabled={isCreatingUser}
                className="rounded px-2 py-1 text-2xl leading-none text-zinc-600 hover:bg-zinc-100 disabled:opacity-50"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="space-y-4">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-zinc-700">
                  Email address
                </span>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={newUser.email}
                  onChange={(event) =>
                    setNewUser((current) => ({
                      ...current,
                      email: event.target.value,
                    }))
                  }
                  className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-200"
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-sm font-medium text-zinc-700">
                  Temporary password
                </span>
                <PasswordInput
                  aria-label="Temporary password"
                  required
                  autoComplete="new-password"
                  value={newUser.password}
                  onChange={(event) => {
                    if (
                      !event.target.value ||
                      event.target.value.length >= 12
                    ) {
                      setCreatePasswordError("");
                    }
                    setNewUser((current) => ({
                      ...current,
                      password: event.target.value,
                    }));
                  }}
                  aria-invalid={Boolean(createPasswordError)}
                  aria-describedby={
                    createPasswordError
                      ? "create-user-password-error"
                      : undefined
                  }
                  className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                    createPasswordError
                      ? "border-red-600 focus:border-red-600 focus:ring-red-100"
                      : "border-zinc-300 focus:border-zinc-500 focus:ring-zinc-200"
                  }`}
                />
                {createPasswordError ? (
                  <span
                    id="create-user-password-error"
                    className="mt-1 block text-sm text-red-700"
                    role="alert"
                  >
                    {createPasswordError}
                  </span>
                ) : (
                  <span className="mt-1 block text-xs text-zinc-500">
                    Must be at least 12 characters.
                  </span>
                )}
              </label>

              <label className="block">
                <span className="mb-1 block text-sm font-medium text-zinc-700">
                  Role
                </span>
                <select
                  value={newUser.role}
                  onChange={(event) =>
                    setNewUser((current) => ({
                      ...current,
                      role: event.target.value,
                    }))
                  }
                  className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900"
                >
                  {(canAssignRoles
                    ? USER_ROLES.filter(
                        (role) =>
                          role !== "ROOT" &&
                          (canManageAdmins || role !== "ADMIN"),
                      )
                    : ["EDITOR", "READER"]
                  ).map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </label>

              {createUserError && (
                <p
                  className="rounded-md bg-red-100 p-3 text-sm text-red-800"
                  role="alert"
                >
                  {createUserError}
                </p>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeCreateDialog}
                  disabled={isCreatingUser}
                  className="rounded-md border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreatingUser}
                  className="rounded-md bg-zinc-800 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isCreatingUser ? "Creating…" : "Create user"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {userPendingDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <section
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-user-title"
            aria-describedby="delete-user-description"
            className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
          >
            <h2
              id="delete-user-title"
              className="mb-3 text-xl font-semibold text-zinc-900"
            >
              Delete account?
            </h2>
            <p id="delete-user-description" className="text-sm text-zinc-700">
              Permanently delete <strong>{userPendingDelete.email}</strong>?
              This cannot be undone.
            </p>
            {deleteError && (
              <p
                className="mt-3 rounded-md bg-red-100 p-3 text-sm text-red-800"
                role="alert"
              >
                {deleteError}
              </p>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setUserPendingDelete(null);
                  setDeleteError("");
                }}
                disabled={deletingUserId !== null}
                className="rounded-md border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteUser}
                disabled={deletingUserId !== null}
                className="rounded-md bg-red-700 px-4 py-2 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-50"
              >
                {deletingUserId ? "Deleting…" : "Delete account"}
              </button>
            </div>
          </section>
        </div>
      )}

      {userPendingSessionInvalidation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <section
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="invalidate-user-sessions-title"
            aria-describedby="invalidate-user-sessions-description"
            className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
          >
            <h2
              id="invalidate-user-sessions-title"
              className="mb-3 text-xl font-semibold text-zinc-900"
            >
              Sign out user?
            </h2>
            <p
              id="invalidate-user-sessions-description"
              className="text-sm text-zinc-700"
            >
              This will immediately sign out{" "}
              <strong>{userPendingSessionInvalidation.email}</strong> from all
              active sessions. They will need to log in again.
            </p>
            {sessionError && (
              <p
                className="mt-3 rounded-md bg-red-100 p-3 text-sm text-red-800"
                role="alert"
              >
                {sessionError}
              </p>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setUserPendingSessionInvalidation(null)}
                disabled={invalidatingUserId !== null}
                className="rounded-md border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() =>
                  handleInvalidateUserSessions(userPendingSessionInvalidation)
                }
                disabled={invalidatingUserId !== null}
                className="rounded-md bg-red-700 px-4 py-2 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-50"
              >
                {invalidatingUserId ? "Signing out…" : "Sign out user"}
              </button>
            </div>
          </section>
        </div>
      )}

      {editingUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeEditDialog();
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-user-title"
            className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
          >
            <div className="mb-5 flex items-center justify-between gap-4">
              <h2
                id="edit-user-title"
                className="text-xl font-semibold text-zinc-900"
              >
                Edit user
              </h2>
              <button
                type="button"
                aria-label="Close dialog"
                onClick={closeEditDialog}
                disabled={isUpdatingUser}
                className="rounded px-2 py-1 text-2xl leading-none text-zinc-600 hover:bg-zinc-100 disabled:opacity-50"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUpdateUser} className="space-y-4">
              {canUpdateUsers && (
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-zinc-700">
                    Email address
                  </span>
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={editUserForm.email}
                    onChange={(event) =>
                      setEditUserForm((current) => ({
                        ...current,
                        email: event.target.value,
                      }))
                    }
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-200"
                  />
                </label>
              )}

              {canUpdateUsers && (
                <div className="rounded-md border border-zinc-200 p-3">
                  <label className="flex items-start gap-2 text-sm font-medium text-zinc-800">
                    <input
                      type="checkbox"
                      checked={editUserForm.mustChangePassword}
                      onChange={(event) =>
                        setEditUserForm((current) => ({
                          ...current,
                          mustChangePassword: event.target.checked,
                        }))
                      }
                      className="mt-0.5"
                    />
                    Require password change at next login
                  </label>
                  <p className="ml-6 mt-1 text-xs text-zinc-600">
                    The user’s current sessions stay active. The change is
                    required the next time they log in.
                  </p>
                </div>
              )}

              {canUpdateUsers && (
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-zinc-700">
                    New password{" "}
                    <span className="font-normal text-zinc-500">
                      (optional)
                    </span>
                  </span>
                  <PasswordInput
                    autoComplete="new-password"
                    value={editUserForm.password}
                    onChange={(event) => {
                      if (
                        !event.target.value ||
                        event.target.value.length >= 12
                      ) {
                        setEditPasswordError("");
                      }
                      setEditUserForm((current) => ({
                        ...current,
                        password: event.target.value,
                      }));
                    }}
                    aria-invalid={Boolean(editPasswordError)}
                    aria-describedby={
                      editPasswordError ? "edit-user-password-error" : undefined
                    }
                    className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                      editPasswordError
                        ? "border-red-600 focus:border-red-600 focus:ring-red-100"
                        : "border-zinc-300 focus:border-zinc-500 focus:ring-zinc-200"
                    }`}
                  />
                  {editPasswordError && (
                    <span
                      id="edit-user-password-error"
                      className="mt-1 block text-sm text-red-700"
                      role="alert"
                    >
                      {editPasswordError}
                    </span>
                  )}
                  <span className="mt-1 block text-xs text-zinc-500">
                    Leave blank to keep the current password.
                  </span>
                </label>
              )}

              {canAssignRoles && (
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-zinc-700">
                    Role
                  </span>
                  <select
                    value={selectedUserRole}
                    onChange={(event) =>
                      setSelectedUserRole(event.target.value)
                    }
                    className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900"
                  >
                    {USER_ROLES.filter(
                      (role) =>
                        role !== "ROOT" &&
                        (canManageAdmins || role !== "ADMIN"),
                    ).map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {editUserError && (
                <p
                  className="rounded-md bg-red-100 p-3 text-sm text-red-800"
                  role="alert"
                >
                  {editUserError}
                </p>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeEditDialog}
                  disabled={isUpdatingUser}
                  className="rounded-md border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUpdatingUser}
                  className="rounded-md bg-zinc-800 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isUpdatingUser ? "Saving…" : "Save changes"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </main>
  );
};

function DateFilter({ label, value, onChange }) {
  const calendarInput = useRef(null);
  const [displayValue, setDisplayValue] = useState(
    formatDateFilterValue(value),
  );
  const parsedValue = parseDateFilterValue(displayValue);
  const isInvalid = displayValue !== "" && !parsedValue;

  useEffect(() => {
    setDisplayValue(formatDateFilterValue(value));
  }, [value]);

  const handleTextChange = (event) => {
    const nextValue = event.target.value;
    setDisplayValue(nextValue);
    if (nextValue === "") onChange("");
    else {
      const parsedDate = parseDateFilterValue(nextValue);
      if (parsedDate) onChange(parsedDate);
    }
  };

  const openCalendar = () => {
    if (typeof calendarInput.current?.showPicker === "function") {
      calendarInput.current.showPicker();
    } else {
      calendarInput.current?.click();
    }
  };

  return (
    <div className="relative mt-2 flex w-full min-w-36 items-center gap-1 font-normal normal-case tracking-normal">
      <input
        type="text"
        inputMode="numeric"
        aria-label={label}
        aria-invalid={isInvalid}
        placeholder="DD.MM.YYYY"
        title="Enter a date in DD.MM.YYYY format"
        value={displayValue}
        onChange={handleTextChange}
        onBlur={() => {
          if (isInvalid) setDisplayValue(formatDateFilterValue(value));
        }}
        className={`min-w-0 w-full rounded border bg-white px-2 py-1 text-xs text-zinc-900 placeholder:text-zinc-400 ${
          isInvalid ? "border-red-500" : "border-zinc-300"
        }`}
      />
      <button
        type="button"
        aria-label={`Open calendar for ${label}`}
        onClick={openCalendar}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50"
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        >
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4M8 3v4M3 10h18" />
        </svg>
      </button>
      <input
        ref={calendarInput}
        type="date"
        lang="de-DE"
        aria-hidden="true"
        tabIndex={-1}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setDisplayValue(formatDateFilterValue(event.target.value));
        }}
        className="pointer-events-none absolute left-0 top-0 h-px w-px opacity-0"
      />
    </div>
  );
}

export default AccountPage;
